import type { SiteConfigFn } from '@wix/thunderbolt-vsm-api-types'
import type { SiteMembersSiteConfig } from '../types'
import { CompTypes, APPS } from '../constants'
import type { SiteMembersClientSpecEntry, TPAPublicClientSpecEntry } from '@wix/thunderbolt-ssr-api'

const membersInfoAppDefId = '14cffd81-5215-0a7f-22f8-074b0e2401fb'

const siteMembersSiteConfig: SiteConfigFn<SiteMembersSiteConfig> = ({ api, funcLib }) => {
	const componentsCountHints = api.rendererModel.getSiteMetaData()?.renderHints?.componentsCount

	const loginSocialBarOnSite = componentsCountHints
		? CompTypes.LoginSocialBar in componentsCountHints || CompTypes.LoginSocialBarE3 in componentsCountHints
		: false

	const hasPlatform = api.features.isActive('platform')
	const hasTpaCommons = api.features.isActive('tpaCommons')

	const pageList = api.rendererModel.getPageList()
	const passwordProtectedPages = api.rendererModel.getPasswordProtectedPages() || []
	const hasProtectedPages = (pageList?.pages || []).some((page) => {
		const isProtected = !page.pageJsonFileName
		const isPasswordProtected = passwordProtectedPages.includes(page.pageId)
		return isProtected && !isPasswordProtected
	})

	const hasSiteMembersComponents = componentsCountHints
		? CompTypes.SocialAuth in componentsCountHints ||
			CompTypes.LoginButton in componentsCountHints ||
			loginSocialBarOnSite
		: true // Default to true if renderHints is missing

	const shouldLoad = hasPlatform || hasTpaCommons || hasProtectedPages || hasSiteMembersComponents

	if (!shouldLoad) {
		return {
			shouldLoadSiteFeature: false,
			shouldLoadSiteConfig: false,
			siteConfig: null,
		}
	}

	const clientSpecMap = api.rendererModel.getClientSpecMap()

	const smApp = (Object.values(clientSpecMap).find((app) => app.type === 'sitemembers') ||
		{}) as SiteMembersClientSpecEntry

	const memberInfo =
		Object.values(clientSpecMap).find(
			(app) => (app as TPAPublicClientSpecEntry).appDefinitionId === membersInfoAppDefId
		) || ({} as TPAPublicClientSpecEntry)
	const memberInfoAppId = memberInfo.applicationId

	const experiments = api.env.getExperiments()
	const deprecateAppIdExperimentOn = !!experiments['specs.thunderbolt.deprecateAppId']

	const memberInfoId = deprecateAppIdExperimentOn ? { membersInfoAppDefId } : { memberInfoAppId }

	const mainPageId = pageList?.mainPageId
	const mainPage = (pageList?.pages || []).find((page) => page.pageId === mainPageId)
	const protectedHomepage = mainPage ? !mainPage.pageJsonFileName : false

	const cookieHeader = api.originalRequest.getHeaders()?.cookie?.[0] || ''
	const cookies = funcLib.parseCookie(cookieHeader)

	const isTemplate = api.rendererModel.getSiteInfo()?.documentType === 'Template'

	const routersConfig = api.rendererModel.getRouters()?.configMap || {}
	let routerPrefix = ''
	for (const router of Object.values(routersConfig)) {
		if (router.appDefinitionId === APPS.PLATFORMIZED_AUTH) {
			routerPrefix = router.prefix || ''
			break
		}
	}

	let isCommunityInstalled = false
	for (const router of Object.values(routersConfig)) {
		if (router.config) {
			try {
				const config = funcLib.parseJSON(router.config)
				if (config && config.type === 'public' && config.patterns && Object.keys(config.patterns).length > 0) {
					isCommunityInstalled = true
					break
				}
			} catch (e) {
				// Ignore parse errors
			}
		}
	}

	const baseUrl = api.publicModel.getExternalBaseUrl()

	const siteConfig: SiteMembersSiteConfig = {
		collectionExposure: smApp.collectionExposure,
		smcollectionId: smApp.smcollectionId,
		smToken: smApp.smtoken || '',
		protectedHomepage,
		sm_efCookie: cookies.sm_ef,
		smSessionCookie: cookies.smSession,
		isTemplate,
		loginSocialBarOnSite,
		routerPrefix,
		isCommunityInstalled,
		baseUrl,
		...memberInfoId,
	}

	return {
		shouldLoadSiteFeature: true,
		shouldLoadSiteConfig: true,
		siteConfig,
	}
}

const name = 'siteMembers'

export const config = { site: siteMembersSiteConfig, name }
